import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, Loader2 } from "lucide-react";

const WaIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;

const waText = (est, company) => {
  const lines = [`*${company} — Estimate ${est.estimate_no}*`, `Date: ${new Date(est.created_at).toLocaleDateString("en-IN")}`, `Customer: ${est.customer_name}`, ""];
  est.vehicles.forEach((v, i) => {
    lines.push(`${i + 1}) *${v.model_name}* (${v.model_code})`);
    lines.push(`Chassis: ${v.chassis_no}${v.colour ? ` · ${v.colour}` : ""}`);
    v.lines.filter((l) => l.included && l.value > 0).forEach((l) => lines.push(`  ${l.label}: ${l.deduction ? "−" : ""}${inr(l.value)}`));
    lines.push(`  *Vehicle Total: ${inr(v.vehicle_total)}*`, "");
  });
  lines.push(`Grand Total: ${inr(est.grand_total)}`);
  if (est.extra_discount > 0) lines.push(`Extra Discount: −${inr(est.extra_discount)}`);
  lines.push(`*Net Total: ${inr(est.net_total)}*`);
  if (est.balance > 0) lines.push(`Balance: ${inr(est.balance)}`);
  if (est.remarks) lines.push(`Remarks: ${est.remarks}`);
  lines.push("", "_Valid for 7 days. Prices from dealership records._");
  return lines.join("\n");
};

export default function EstimatePrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [est, setEst] = useState(null);
  const [company, setCompany] = useState("OPM Bajaj");
  const [sharing, setSharing] = useState(false);
  const sheetRef = useRef(null);

  useEffect(() => { api.get(`/estimates/${id}`).then((r) => setEst(r.data)).catch(() => {}); }, [id]);
  useEffect(() => { api.get("/settings/company").then((r) => setCompany(r.data.name)).catch(() => {}); }, []);

  if (!est) return <div className="min-h-screen bg-white flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-emerald-600 border-t-transparent animate-spin" /></div>;

  const payRows = [
    ["Received — Cash", est.received_cash], ["Received — Bank", est.received_bank], ["Advance", est.advance],
    ["Due on Finance", est.due_finance], ["Due on Customer", est.due_customer], ["Extra Discount", est.extra_discount],
  ].filter(([, v]) => v > 0);

  return (
    <div className="min-h-screen bg-slate-200 print:bg-white">
      <div className="max-w-3xl mx-auto p-4 print:p-0">
        <div className="flex justify-between items-center mb-3 print:hidden">
          <Button data-testid="estimate-back-btn" variant="outline" size="sm" onClick={() => navigate("/")} className="border-slate-400 text-slate-700">
            <ArrowLeft className="h-4 w-4 mr-1" /> Dashboard
          </Button>
          <div className="flex gap-2">
            <Button data-testid="estimate-whatsapp-btn" size="sm" disabled={sharing}
              onClick={async () => {
                setSharing(true);
                try {
                  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import("html2canvas"), import("jspdf")]);
                  const canvas = await html2canvas(sheetRef.current, { scale: 2, backgroundColor: "#ffffff" });
                  const pdf = new jsPDF({ unit: "mm", format: "a4" });
                  const w = 210, h = (canvas.height * w) / canvas.width;
                  pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", 0, 0, w, Math.min(h, 297));
                  const file = new File([pdf.output("blob")], `${est.estimate_no}.pdf`, { type: "application/pdf" });
                  if (navigator.canShare && navigator.canShare({ files: [file] })) {
                    await navigator.share({ files: [file], title: est.estimate_no, text: `${company} — Estimate ${est.estimate_no}` });
                  } else {
                    pdf.save(`${est.estimate_no}.pdf`);
                    window.open(`https://wa.me/?text=${encodeURIComponent(waText(est, company))}`, "_blank");
                  }
                } catch (e) {
                  if (e?.name !== "AbortError") window.open(`https://wa.me/?text=${encodeURIComponent(waText(est, company))}`, "_blank");
                } finally { setSharing(false); }
              }}
              className="bg-[#25D366] hover:bg-[#1ebe5b] text-white">
              {sharing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <WaIcon className="h-4 w-4 mr-1.5" />} WhatsApp PDF
            </Button>
            <Button data-testid="estimate-print-btn" size="sm" onClick={() => window.print()} className="bg-emerald-600 hover:bg-emerald-500 text-white">
              <Printer className="h-4 w-4 mr-1.5" /> Print / Save PDF
            </Button>
          </div>
        </div>
        <div ref={sheetRef} data-testid="estimate-document" className="bg-white text-slate-900 rounded-lg print:rounded-none shadow print:shadow-none p-6 sm:p-8">
          <div className="flex flex-wrap justify-between items-start gap-2 border-b-2 border-slate-900 pb-4 mb-4">
            <div>
              <h1 className="text-2xl font-extrabold" style={{ fontFamily: "Outfit, sans-serif" }}>ESTIMATE</h1>
              <p className="text-sm text-slate-600">{company}</p>
            </div>
            <div className="text-right text-sm">
              <p className="font-mono font-bold">{est.estimate_no}</p>
              <p className="text-slate-600">{new Date(est.created_at).toLocaleDateString("en-IN")}</p>
              <p className="text-slate-600">By: {est.created_by}</p>
            </div>
          </div>
          <p className="text-sm mb-4"><span className="text-slate-500">To:</span> <b>{est.customer_name}</b></p>
          {est.vehicles.map((v) => (
            <div key={v.chassis_no} className="mb-4 border border-slate-300 rounded-lg overflow-hidden">
              <div className="bg-slate-100 px-4 py-2 flex flex-wrap justify-between gap-1 text-sm">
                <b>{v.model_name} ({v.model_code})</b>
                <span className="font-mono">{v.chassis_no}{v.colour ? ` · ${v.colour}` : ""}</span>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {v.lines.filter((l) => l.included).map((l) => (
                    <tr key={l.key} className="border-t border-slate-200">
                      <td className="px-4 py-1.5">{l.label}</td>
                      <td className={`px-4 py-1.5 text-right font-mono ${l.deduction ? "text-rose-600" : ""}`}>
                        {l.deduction && l.value > 0 ? "−" : ""}{inr(l.value)}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-slate-400 bg-slate-50">
                    <td className="px-4 py-2 font-bold">Vehicle Total</td>
                    <td className="px-4 py-2 text-right font-mono font-bold">{inr(v.vehicle_total)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ))}
          <div className="flex justify-end">
            <table className="text-sm w-full sm:w-80">
              <tbody>
                <tr><td className="py-1 text-slate-600">Grand Total</td><td className="py-1 text-right font-mono">{inr(est.grand_total)}</td></tr>
                {payRows.map(([l, v]) => (
                  <tr key={l}><td className="py-1 text-slate-600">{l}</td><td className="py-1 text-right font-mono">{inr(v)}</td></tr>
                ))}
                <tr className="border-t-2 border-slate-900"><td className="py-2 font-bold">Net Total</td><td className="py-2 text-right font-mono font-bold text-lg">{inr(est.net_total)}</td></tr>
                <tr><td className="py-1 font-semibold text-slate-700">Balance</td><td className={`py-1 text-right font-mono font-semibold ${est.balance > 0 ? "text-rose-600" : "text-emerald-600"}`}>{inr(est.balance)}</td></tr>
              </tbody>
            </table>
          </div>
          {est.remarks && <p className="text-xs text-slate-600 mt-4 border-t border-slate-200 pt-3"><b>Remarks:</b> {est.remarks}</p>}
          <p className="text-[10px] text-slate-400 mt-6">This is a system-generated estimate. Prices pulled from dealership records. Valid for 7 days.</p>
        </div>
      </div>
    </div>
  );
}
